import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { actionOnMessage } from '@skynet/data-ws-mm';
import { parseResponse } from '@skynet/data-ws-core';
import { listen as listenLoans } from 'src/store/loans/actions';
import { listen as listenRetainers } from 'src/store/retainers/actions';
import type { ArbitrageInfoState, JsonArbitrageInfo } from './types';
import { ArbitrageInfoCommands } from './types';
import { getArbitrageData } from './actions';

const allowedCommands = Object.values(ArbitrageInfoCommands);

export const initialState: ArbitrageInfoState = {
  total: null,
  accounts: [],
  // Data is always requested on mount, so start in a loading state to avoid an
  // empty-state flash before the initial Listen command is dispatched.
  status: 'pending',
  error: null,
  subscribedId: null,
};

/**
 * Applies a `Listen` for the arbitrage-info section.
 *
 * `entityId` is the project or loan the subscription is opened for. When it
 * differs from the one currently subscribed the data on screen belongs to the
 * previous entity, so it is dropped right away instead of lingering until the
 * new response arrives. A repeat `Listen` for the same entity (the polling
 * interval) keeps the data so the card doesn't blink on every refresh.
 */
function applyListen(
  state: ArbitrageInfoState,
  section: string,
  entityId: number | null,
  isInitCall: boolean,
) {
  if (section !== 'arbitrage-info') return;

  if (entityId !== state.subscribedId) {
    state.total = null;
    state.accounts = [];
    state.error = null;
    state.subscribedId = entityId;
    state.status = 'pending';
    return;
  }

  if (isInitCall) {
    state.status = 'pending';
  }
}

const arbitrageInfoSlice = createSlice({
  name: 'arbitrageInfo',
  initialState,
  reducers: {
    /**
     * Clears cached data so switching projects doesn't briefly show the
     * previous project's total/details before the new Listen response arrives.
     * This also closes the subscription (`subscribedId`), which makes any push
     * still arriving for the previous project be ignored — see
     * `actionOnMessage`.
     *
     * Takes the entity now on screen, and leaves the state alone when a
     * subscription for it is already open — re-entering the page for the
     * project that was last subscribed does not make its data stale, and
     * closing the subscription there would discard the pushes that keep
     * arriving on it until the new (debounced) Listen goes out, leaving the
     * card empty for no reason.
     */
    reset: (state, action: PayloadAction<number | undefined>): ArbitrageInfoState =>
      action.payload !== undefined && state.subscribedId === action.payload ? state : initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(listenRetainers, (state, action) => {
      const { section, projectID } = action.payload;
      applyListen(state, section, projectID ?? null, Boolean(action.meta.isInitCall));
    });

    builder.addCase(listenLoans, (state, action) => {
      const { section, loanID } = action.payload;
      applyListen(state, section, loanID ?? null, Boolean(action.meta.isInitCall));
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      console.debug(
        `shared/arbitrage-info/processing ${actionOnMessage.toString()}`,
        action,
        allowedCommands,
      );

      const { command } = json;

      if (error) {
        state.error = error;

        switch (errorCommand) {
          case ArbitrageInfoCommands.GetArbitrage:
            state.status = 'error';
            break;
          case undefined:
          default:
        }

        return;
      }

      if (command === ArbitrageInfoCommands.ArbitrageInfo) {
        // The push carries no project/loan id, so it can only be attributed to
        // the subscription it arrives on. With no subscription open the page
        // has just switched entity and the new Listen (debounced) hasn't gone
        // out yet — the server is still pushing the previous project's data.
        // Rendering it would show the previous project's arbitrage under the
        // newly selected one.
        //
        // `arbitrage-info` is not one of `noIdSections`, so its Listen always
        // carries a projectID/loanID and `subscribedId` holds a number as soon
        // as one goes out: `null` means "no Listen since the last reset", never
        // "subscribed to nothing". Making it a no-id section would need this
        // guard reworked, or every push would be dropped.
        //
        // Nothing legitimate is lost here. A push only arrives on a
        // subscription, and `reset` closes one only when the entity on screen
        // changed — so in this window the open subscription, and therefore the
        // push, belongs to the entity that was just left.
        if (state.subscribedId === null) return;

        const { accounts, total } = json as JsonArbitrageInfo;
        state.total = total;
        state.accounts = accounts;
        state.status = 'success';
      }
    });
  },
});

export const arbitrageInfoActions = { ...arbitrageInfoSlice.actions, getArbitrageData };

export default arbitrageInfoSlice.reducer;
